// v3E.1 — Reminder dashboard (replaces v3A placeholder)
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    Plus, Pill, ShieldAlert, RefreshCw, AlertTriangle, TrendingUp, Flame, Box, Loader2,
} from "lucide-react";
import Layout from "../components/layout/Layout";
import AuthGuard from "../components/AuthGuard";
import EmptyState from "../components/EmptyState";
import TodayDosesWidget from "../components/reminder/TodayDosesWidget";
import { api } from "../api/client";

const FILTER_CHIPS = [
    { id: "all", label: "All" },
    { id: "today", label: "Today" },
    { id: "critical", label: "Critical" },
    { id: "refill_due", label: "Refill due" },
    { id: "inactive", label: "Inactive" },
];

const Kpi = ({ icon: Icon, label, value, tone = "brand", testId }) => {
    const tones = {
        brand: "bg-brand-50 text-brand-700",
        emerald: "bg-emerald-50 text-emerald-700",
        amber: "bg-amber-50 text-amber-700",
        rose: "bg-rose-50 text-rose-700",
    };
    return (
        <div className="rounded-2xl border border-zinc-100 bg-white p-4 shadow-sm" data-testid={testId}>
            <div className="flex items-center gap-3">
                <div className={`grid h-9 w-9 place-items-center rounded-lg ${tones[tone] || tones.brand}`}>
                    <Icon className="h-4 w-4" />
                </div>
                <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">{label}</p>
                    <p className="text-xl font-semibold text-zinc-900">{value ?? "—"}</p>
                </div>
            </div>
        </div>
    );
};

const RemindersContent = () => {
    const [meds, setMeds] = useState([]);
    const [adherence, setAdherence] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [medsResp, adhResp] = await Promise.all([
                api.get("/reminder/medications", { params: { active: filter === "inactive" ? false : true, page_size: 100 } }),
                api.get("/reminder/adherence/me"),
            ]);
            setMeds(medsResp.data?.items || []);
            setAdherence(adhResp.data);
        } finally {
            setLoading(false);
        }
    }, [filter]);

    useEffect(() => { load(); }, [load]);

    const filtered = useMemo(() => {
        if (filter === "all" || filter === "inactive") return meds;
        if (filter === "critical") return meds.filter((m) => m.is_critical);
        if (filter === "today") return meds.filter((m) => m.next_dose && m.next_dose.scheduled_date === new Date().toISOString().slice(0, 10));
        if (filter === "refill_due") return meds.filter((m) => m._refill_due);
        return meds;
    }, [meds, filter]);

    const refillDueCount = useMemo(() => meds.filter((m) => m._refill_due).length, [meds]);

    return (
        <div className="space-y-6">
            {/* Today widget */}
            <TodayDosesWidget />

            {/* KPI strip */}
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                <Kpi icon={TrendingUp} label="30-day adherence" value={adherence?.monthly_compliance_pct != null ? `${adherence.monthly_compliance_pct}%` : "—"} tone="emerald" testId="kpi-adherence" />
                <Kpi icon={Pill} label="Active meds" value={meds.filter((m) => m.active).length} tone="brand" testId="kpi-active-meds" />
                <Kpi icon={Box} label="Refills due" value={refillDueCount} tone="amber" testId="kpi-refills-due" />
                <Kpi icon={Flame} label="Streak" value={adherence?.current_streak_days != null ? `${adherence.current_streak_days}d` : "—"} tone="rose" testId="kpi-streak" />
            </div>

            {/* Filter chips */}
            <div className="flex flex-wrap items-center gap-2" data-testid="reminders-filter-chips">
                {FILTER_CHIPS.map((c) => (
                    <button
                        key={c.id}
                        onClick={() => setFilter(c.id)}
                        className={`rounded-full border px-3 py-1 text-xs font-medium transition ${
                            filter === c.id
                                ? "border-brand-600 bg-brand-600 text-white"
                                : "border-zinc-200 bg-white text-zinc-700 hover:border-brand-300"
                        }`}
                        data-testid={`filter-${c.id}`}
                    >
                        {c.label}
                    </button>
                ))}
                <button onClick={load} className="ml-auto inline-flex items-center gap-1 rounded-full border border-zinc-200 bg-white px-3 py-1 text-xs text-zinc-600 hover:border-brand-300" data-testid="reminders-refresh">
                    <RefreshCw className="h-3 w-3" /> Refresh
                </button>
            </div>

            {/* Medications */}
            {loading ? (
                <div className="space-y-3">
                    {[1, 2].map((i) => <div key={i} className="h-28 skeleton rounded-2xl" />)}
                </div>
            ) : filtered.length === 0 ? (
                <EmptyState
                    icon={<Pill className="h-10 w-10 text-brand-600" />}
                    title="No medications yet"
                    description="Add your first medicine and we'll remind you on time, every time."
                    action={
                        <Link to="/reminders/add" className="btn-brand" data-testid="reminders-add-cta">
                            <Plus className="h-4 w-4" /> Add medicine
                        </Link>
                    }
                />
            ) : (
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    {filtered.map((m) => (
                        <Link
                            key={m.id}
                            to={`/reminders/${m.id}`}
                            className="group flex items-start gap-3 rounded-2xl border border-zinc-100 bg-white p-4 transition hover:border-brand-200 hover:shadow-sm"
                            data-testid={`med-card-${m.id}`}
                        >
                            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-brand-50 to-violet-50 text-brand-700">
                                <Pill className="h-5 w-5" />
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-1.5">
                                    <h3 className="font-semibold text-zinc-900">{m.medicine_name}</h3>
                                    {m.is_critical && (
                                        <span className="inline-flex items-center gap-0.5 rounded-full bg-rose-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-rose-700" title={`Critical: ${m.critical_match}`}>
                                            <ShieldAlert className="h-2.5 w-2.5" /> critical
                                        </span>
                                    )}
                                    {!m.active && (
                                        <span className="rounded-full bg-zinc-100 px-1.5 py-0.5 text-[10px] font-semibold text-zinc-600">paused</span>
                                    )}
                                </div>
                                <p className="mt-0.5 text-xs text-zinc-500">
                                    {[m.strength, m.dosage_quantity, m.medicine_type].filter(Boolean).join(" · ")}
                                </p>
                                {m.next_dose ? (
                                    <p className="mt-1 text-[11px] font-medium text-brand-700">
                                        Next: {new Date(m.next_dose.scheduled_at).toLocaleString()} ({m.next_dose.slot_label})
                                    </p>
                                ) : (
                                    <p className="mt-1 text-[11px] italic text-zinc-400">No upcoming dose scheduled</p>
                                )}
                            </div>
                        </Link>
                    ))}
                </div>
            )}
        </div>
    );
};

const Reminders = () => (
    <Layout>
        <section className="mx-auto max-w-5xl px-6 py-10 lg:px-12">
            <header className="mb-8 flex items-end justify-between">
                <div>
                    <p className="label-uppercase">PILLS Reminder</p>
                    <h1 className="mt-1 text-3xl font-semibold tracking-tight text-zinc-900 sm:text-4xl">My medications</h1>
                    <p className="mt-1 text-sm text-zinc-500">Smart reminders, adherence tracking, and one-click refills.</p>
                </div>
                <Link to="/reminders/add" className="btn-brand !py-2 !px-4 !text-sm" data-testid="reminders-add-btn">
                    <Plus className="h-4 w-4" /> Add medicine
                </Link>
            </header>
            <AuthGuard>
                <RemindersContent />
            </AuthGuard>
        </section>
    </Layout>
);

export default Reminders;
